import { z } from "zod";
export const EditLevelSchema = z.enum(["L0", "L1", "L2", "L3", "L4", "L5"]);
export const L1_GOVERNED_MEDIA_ROLES = [
    "proof",
    "credential",
    "review",
    "award",
    "license",
    "before_after",
    "claim_supporting",
];
export const EditMatrixPolicyV1Schema = z
    .object({
    schemaVersion: z.literal("edit-matrix-policy.v1.0"),
    policyVersion: z.string().min(1),
    fieldRules: z.array(z
        .object({
        fieldPath: z.string().min(1),
        allowedLevel: EditLevelSchema,
    })
        .strict()),
    l1Allowlist: z.array(z.string()).default([
        "business.primaryPhone",
        "business.primaryEmail",
        "operations.hours",
        "operations.serviceAreas",
        "operations.locations",
        "services",
        "content.offers",
        "content.announcements",
        "assets",
    ]),
    l1MediaRequiresRights: z.literal(true),
    l1ForbiddenMediaRoles: z.array(z.string()).default([...L1_GOVERNED_MEDIA_ROLES]),
})
    .strict();
export const EditPermissionMatrixV1Schema = z
    .object({
    schemaVersion: z.literal("edit-permission-matrix.v1.0"),
    agencyLevels: z.array(EditLevelSchema),
    clientLevels: z.array(EditLevelSchema).default(["L0", "L1"]),
})
    .strict();
export const SafeEditPatchV1Schema = z
    .object({
    schemaVersion: z.literal("safe-edit-patch.v1.0"),
    fieldPath: z.string().min(1),
    newValue: z.unknown(),
    mediaRole: z.string().optional(),
    rightsStatus: z.string().optional(),
})
    .strict()
    .superRefine((data, ctx) => {
    if (data.fieldPath.includes("mediaAsset") || data.mediaRole) {
        const role = (data.mediaRole ?? "").toLowerCase();
        for (const forbidden of L1_GOVERNED_MEDIA_ROLES) {
            if (role.includes(forbidden)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `L1 SafeEdit rejects governed media role: ${forbidden}`,
                    path: ["mediaRole"],
                });
            }
        }
        if (!data.rightsStatus?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "L1 mediaAsset replacement requires rights/permission status",
                path: ["rightsStatus"],
            });
        }
    }
});
export const AgencyEditPatchV1Schema = z
    .object({
    schemaVersion: z.literal("agency-edit-patch.v1.0"),
    patches: z.array(z.object({ fieldPath: z.string().min(1), newValue: z.unknown() }).strict()),
    editLevel: EditLevelSchema,
})
    .strict();
export const EditAuditEventV1Schema = z
    .object({
    schemaVersion: z.literal("edit-audit-event.v1.0"),
    eventId: z.string().min(1),
    fieldPath: z.string().min(1),
    editLevel: EditLevelSchema,
    actor: z.string().min(1),
    occurredAt: z.string().datetime(),
})
    .strict();
export const EditImpactReportV1Schema = z
    .object({
    schemaVersion: z.literal("edit-impact-report.v1.0"),
    requiresRegeneration: z.boolean(),
    requiresQgRerun: z.boolean(),
    invalidatesApproval: z.boolean(),
    affectedRoutes: z.array(z.string()).default([]),
})
    .strict();
export const EditRiskTierSchema = z.enum(["GREEN", "YELLOW", "RED"]);
export const EditActorSchema = z.enum(["CLIENT", "AGENCY", "SYSTEM"]);
export const EditQaImpactSchema = z.enum(["NONE", "REGENERATE", "FULL_QG"]);
export const EditApprovalPolicySchema = z.enum(["AUTO_CLIENT_L1", "AGENCY_REVIEW", "PUBLISH_GATE"]);
export const EditFieldDeclarationV1Schema = z
    .object({
    fieldPath: z.string().min(1),
    allowedLevel: EditLevelSchema,
    actor: EditActorSchema,
    riskTier: EditRiskTierSchema,
    qaImpact: EditQaImpactSchema,
    approvalPolicy: EditApprovalPolicySchema,
    validatorId: z.string().min(1),
})
    .strict();
export const DEFAULT_EDIT_FIELD_DECLARATIONS = EditFieldDeclarationV1Schema.array().parse([
    {
        fieldPath: "business.primaryPhone",
        allowedLevel: "L1",
        actor: "CLIENT",
        riskTier: "GREEN",
        qaImpact: "REGENERATE",
        approvalPolicy: "AUTO_CLIENT_L1",
        validatorId: "phone_plaintext",
    },
    {
        fieldPath: "business.primaryEmail",
        allowedLevel: "L1",
        actor: "CLIENT",
        riskTier: "GREEN",
        qaImpact: "REGENERATE",
        approvalPolicy: "AUTO_CLIENT_L1",
        validatorId: "email_plaintext",
    },
    {
        fieldPath: "operations.hours",
        allowedLevel: "L1",
        actor: "CLIENT",
        riskTier: "YELLOW",
        qaImpact: "REGENERATE",
        approvalPolicy: "AUTO_CLIENT_L1",
        validatorId: "hours_structured",
    },
    {
        fieldPath: "operations.serviceAreas",
        allowedLevel: "L1",
        actor: "CLIENT",
        riskTier: "YELLOW",
        qaImpact: "REGENERATE",
        approvalPolicy: "AUTO_CLIENT_L1",
        validatorId: "service_areas_structured",
    },
    {
        fieldPath: "operations.locations",
        allowedLevel: "L1",
        actor: "CLIENT",
        riskTier: "YELLOW",
        qaImpact: "REGENERATE",
        approvalPolicy: "AUTO_CLIENT_L1",
        validatorId: "locations_structured",
    },
    {
        fieldPath: "services",
        allowedLevel: "L1",
        actor: "CLIENT",
        riskTier: "YELLOW",
        qaImpact: "REGENERATE",
        approvalPolicy: "AUTO_CLIENT_L1",
        validatorId: "services_structured",
    },
    {
        fieldPath: "content.offers",
        allowedLevel: "L1",
        actor: "CLIENT",
        riskTier: "YELLOW",
        qaImpact: "REGENERATE",
        approvalPolicy: "AUTO_CLIENT_L1",
        validatorId: "offers_structured",
    },
    {
        fieldPath: "content.announcements",
        allowedLevel: "L1",
        actor: "CLIENT",
        riskTier: "GREEN",
        qaImpact: "REGENERATE",
        approvalPolicy: "AUTO_CLIENT_L1",
        validatorId: "announcements_structured",
    },
    {
        fieldPath: "assets",
        allowedLevel: "L1",
        actor: "CLIENT",
        riskTier: "YELLOW",
        qaImpact: "REGENERATE",
        approvalPolicy: "AUTO_CLIENT_L1",
        validatorId: "assets_rights_gated",
    },
    {
        fieldPath: "trust.claims",
        allowedLevel: "L4",
        actor: "AGENCY",
        riskTier: "RED",
        qaImpact: "FULL_QG",
        approvalPolicy: "PUBLISH_GATE",
        validatorId: "agency_trust_review",
    },
]);
export const DEFAULT_EDIT_MATRIX_POLICY = EditMatrixPolicyV1Schema.parse({
    schemaVersion: "edit-matrix-policy.v1.0",
    policyVersion: "EditMatrixPolicyV1",
    l1MediaRequiresRights: true,
    fieldRules: DEFAULT_EDIT_FIELD_DECLARATIONS.map((d) => ({
        fieldPath: d.fieldPath,
        allowedLevel: d.allowedLevel,
    })),
});
//# sourceMappingURL=edit-matrix.js.map